import React, { useState } from 'react';
import { Play, Upload, CheckCircle, Lock, AlertCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const STATUS_CONFIG = {
  NOT_STARTED: {
    color: 'bg-gray-100 text-gray-700',
    icon: Clock,
    label: 'Not Started'
  },
  IN_PROGRESS: {
    color: 'bg-blue-100 text-blue-700',
    icon: Play,
    label: 'In Progress'
  },
  REVIEW: {
    color: 'bg-amber-100 text-amber-700',
    icon: AlertCircle,
    label: 'In Review'
  },
  APPROVED: {
    color: 'bg-green-100 text-green-700',
    icon: CheckCircle,
    label: 'Approved'
  },
  DONE: {
    color: 'bg-green-100 text-green-700',
    icon: CheckCircle,
    label: 'Complete'
  },
  BLOCKED: {
    color: 'bg-red-100 text-red-700',
    icon: Lock,
    label: 'Blocked'
  }
};

const ROLE_COLORS = {
  'Author': 'bg-blue-100 text-blue-700',
  'Reviewer': 'bg-green-100 text-green-700',
  'Compliance Officer': 'bg-purple-100 text-purple-700',
  'Technical SME': 'bg-amber-100 text-amber-700',
  'Approver': 'bg-red-100 text-red-700',
  'Safety Officer': 'bg-orange-100 text-orange-700',
  'Clinical SME': 'bg-pink-100 text-pink-700'
};

export default function WorkflowStepCard({
  step,
  stepNumber,
  status,
  canStart,
  blockedReason,
  onStart,
  onUploadArtifact,
  onComplete,
  artifacts = []
}) {
  const [showArtifactUpload, setShowArtifactUpload] = useState(false);
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_STARTED;
  const StatusIcon = statusConfig.icon;
  
  const canUpload = status === 'IN_PROGRESS' || status === 'REVIEW';
  const canComplete = status === 'IN_PROGRESS' || status === 'REVIEW';
  const isDone = status === 'DONE' || status === 'APPROVED';

  return (
    <Card className={`${isDone ? 'border-green-200 bg-green-50/30' : ''}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${isDone ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
              {isDone ? <CheckCircle className="h-5 w-5" /> : stepNumber}
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg mb-2">{step.name}</CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={ROLE_COLORS[step.role_owner] || 'bg-gray-100 text-gray-700'}>
                  {step.role_owner}
                </Badge>
                <Badge className={statusConfig.color}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {statusConfig.label}
                </Badge>
                {step.time_expectation && (
                  <Badge variant="outline" className="text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    {step.time_expectation}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Description */}
        <div className="space-y-3 mb-4">
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-1">What to Do:</p>
            <p className="text-sm text-gray-600">{step.description}</p>
          </div>
          
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-1">Why It Matters:</p>
            <p className="text-sm text-gray-600">{step.why_it_matters}</p>
          </div>
          
          {step.acceptance_criteria && step.acceptance_criteria.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-1">Done When:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                {step.acceptance_criteria.map((criteria, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{criteria}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Artifacts */}
        {artifacts.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-semibold text-gray-700 mb-2">Uploaded Artifacts:</p>
            <ul className="space-y-1">
              {artifacts.map(artifact => (
                <li key={artifact.id} className="text-sm text-gray-600 flex items-center">
                  <Upload className="h-3 w-3 mr-2 text-green-600" />
                  {artifact.filename}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-2">
          {status === 'NOT_STARTED' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex-1">
                    <Button
                      onClick={onStart}
                      disabled={!canStart}
                      className="w-full"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Start Step
                    </Button>
                  </div>
                </TooltipTrigger>
                {!canStart && blockedReason && (
                  <TooltipContent>
                    <p>{blockedReason}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
          
          {canUpload && (
            <Button
              onClick={() => setShowArtifactUpload(true)}
              variant="outline"
              className="flex-1"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Artifact
            </Button>
          )}
          
          {canComplete && (
            <Button
              onClick={onComplete}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete Step
            </Button>
          )}
        </div>
      </CardContent>

      {/* Artifact Upload Modal */}
      {showArtifactUpload && (
        <ArtifactUploadModal
          stepName={step.name}
          onUpload={(file) => {
            onUploadArtifact(file);
            setShowArtifactUpload(false);
          }}
          onClose={() => setShowArtifactUpload(false)}
        />
      )}
    </Card>
  );
}

function ArtifactUploadModal({ stepName, onUpload, onClose }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Upload Artifact for {stepName}
        </h3>
        
        <div className="mb-4">
          <input
            type="file"
            onChange={(e) => setFile(e.target.files[0])}
            className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>
        
        <div className="flex space-x-2">
          <Button onClick={onClose} variant="outline" className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>
    </div>
  );
}
